/**
 * Soma valores ao resumo por método de pagamento.
 *
 * ─── PORQUE EXISTE ──────────────────────────────────────────────────────────
 *
 * O resumo era construído a partir de um objeto com os nove métodos conhecidos
 * escritos à mão, e cada linha somava assim:
 *
 *     quantiaPorMetodoPagamento[parcela.metodoPagamento] += parcela.quantiaPaga;
 *
 * Basta o método não estar nessa lista para o acesso devolver `undefined`, e
 * `undefined + 500` é `NaN`. O resultado ficava guardado sob a chave literal
 * "undefined" e saía impresso no fluxo de caixa como:
 *
 *     UNDEFINED : NaN$00
 *
 * Duas coisas o provocam, e nenhuma é rara: um registo antigo sem método
 * preenchido, e um método de pagamento acrescentado depois de esta lista ter
 * sido escrita — quem o acrescenta não tem como saber que há uma segunda lista
 * a repetir a primeira.
 *
 * O pior nem é o NaN à vista: é que esse dinheiro deixa de estar somado em
 * qualquer das linhas do resumo, enquanto continua a contar para o saldo, que é
 * calculado à parte. O resumo deixa de fechar com o saldo e não se percebe
 * porquê.
 *
 * ─── O QUE FAZ ──────────────────────────────────────────────────────────────
 *
 * Cria a linha se ela ainda não existir — um método novo aparece no resumo com
 * o seu nome, sem ninguém ter de vir aqui acrescentá-lo — e junta o que não tem
 * método numa linha própria, em vez de o deixar cair.
 *
 * @param {object} acumulador  o objeto do resumo, alterado no sítio
 * @param {string} metodo      `registo.metodoPagamento`, que pode vir vazio
 * @param {number} valor       positivo a somar, negativo a subtrair
 */
export const SEM_METODO = "Sem método indicado";

export const somarAoMetodo = (acumulador, metodo, valor) => {
  const chave =
    metodo && String(metodo).trim() !== "" ? String(metodo) : SEM_METODO;

  const atual = Number(acumulador[chave]) || 0;
  const soma = Number(valor) || 0;

  acumulador[chave] = atual + soma;
};

/**
 * Linhas do resumo que vale a pena mostrar.
 *
 * Descarta as que estão a zero — um resumo com nove linhas a zero não ajuda
 * ninguém — e as que não são números, que é a rede por baixo do `somarAoMetodo`:
 * se um NaN voltar a entrar por outro caminho, não chega ao papel.
 */
export const linhasDoResumo = (resumo) =>
  Object.entries(resumo || {}).filter(
    ([, valor]) => Number.isFinite(Number(valor)) && Number(valor) !== 0,
  );
